import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Popover from '@mui/material/Popover';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import AddProjekttervezesDialog from './AddProjekttervezesDialog';
import AddProjekttervezesFeladatDialog, { feladatDialogValueToPayload } from './AddProjekttervezesFeladatDialog';
import AddProjekttervezesCsoportDialog, { csoportDialogValueToPayload } from './AddProjekttervezesCsoportDialog';
import {
  CHILD_ROW_BG,
  GanttCsoportBar,
  GanttFeladatBar,
  GanttRowBars,
  GanttTimelineControls,
  GanttTimelineHeader,
  GanttViewportMeasure,
  GANTT_ROW_HEIGHT,
  COMPLETED_ROW_BG,
  ROW_DIVIDER_COLOR,
  useGanttTimeline,
} from './ProjekttervezesGantt';
import {
  PROJEKTTERVEZES_FELADAT_STATUS_LABELS,
  ProjekttervezesCsoport,
  ProjekttervezesFeladat,
  ProjekttervezesFocus,
  ProjekttervezesMunkaszamOption,
  ProjekttervezesSor,
  projekttervezesService,
} from '../services/projekttervezesService';
import {
  buildProjekttervezesFlatRows,
  computeCsoportDateRange,
  FlatRow,
  getMergedSiblingItems,
} from '../utils/projekttervezesTree';
import { permissionService } from '../services/permissionService';
import {
  formatDisplayDate,
  parseApiDate,
  toApiDate,
} from '../utils/workDays';
import { getApiErrorMessage } from '../services/apiService';

const LEFT_COLUMNS = [
  { key: 'actions', label: '', width: 126 },
  { key: 'munkaszam', label: 'Munkaszám', width: 108 },
  { key: 'targy', label: 'Munka tárgya', width: 192, truncate: true },
  { key: 'felelos', label: 'Felelős', width: 96, truncate: true },
  { key: 'kezdet', label: 'Projekt kezdete', width: 108 },
  { key: 'tenylegesKezdet', label: 'Tényleges kezdés', width: 108 },
  { key: 'tervezettBefejezes', label: 'Tervezett befejezés', width: 118 },
  { key: 'hatralevo', label: 'Hátralévő nap', width: 88 },
  { key: 'tenylegesBefejezes', label: 'Tényleges befejezés', width: 108 },
  { key: 'haladas', label: 'Haladás %', width: 92 },
] as const;

const LEFT_TABLE_MIN_WIDTH = LEFT_COLUMNS.reduce((sum, col) => sum + col.width, 0);

const LEFT_PANEL_MAX_WIDTH = '46%';
const ARCHIVED_ROW_BG = '#e3f2fd';

const EXPANDED_ROW_BORDER_COLOR = '#1565c0';

const ExpandedRowOutline: React.FC = () => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      border: `2px solid ${EXPANDED_ROW_BORDER_COLOR}`,
      pointerEvents: 'none',
      zIndex: 3,
      boxSizing: 'border-box',
    }}
  />
);

interface FeladatDialogState {
  open: boolean;
  mode: 'create' | 'edit';
  tetelId: number;
  csoportId: number | null;
  feladat: ProjekttervezesFeladat | null;
}

interface CsoportDialogState {
  open: boolean;
  mode: 'create' | 'edit';
  tetelId: number;
  parentCsoportId: number | null;
  csoport: ProjekttervezesCsoport | null;
}

interface AddItemMenuState {
  anchorEl: HTMLElement;
  tetelId: number;
  parentCsoportId: number | null;
}

interface DeleteConfirmState {
  open: boolean;
  kind: 'project' | 'group' | 'task';
  tetelId: number;
  feladatId?: number;
  csoportId?: number;
  label: string;
}

type ProjektFilterMode = 'none' | 'dateRange' | 'munkaszam';

interface ArchiveConfirmState {
  open: boolean;
  tetelId: number;
  label: string;
  nextArchivedState: boolean;
}

const headerCellSx = {
  px: 0.5,
  py: 0.75,
  backgroundColor: '#f8f9fb',
  borderRight: `1px solid ${ROW_DIVIDER_COLOR}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontWeight: 600,
  fontSize: 11,
  color: '#5f6b7a',
  textAlign: 'center' as const,
  lineHeight: 1.2,
};

const bodyCellSx = {
  px: 0.5,
  py: 0.5,
  borderRight: `1px solid ${ROW_DIVIDER_COLOR}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'hidden',
  textAlign: 'center' as const,
};

interface CompactTableDatePickerProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  disabled?: boolean;
}

const CompactTableDatePicker: React.FC<CompactTableDatePickerProps> = ({ value, onChange, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (disabled) {
    return (
      <Typography variant="body2" sx={{ fontSize: 12 }}>
        {formatDisplayDate(value)}
      </Typography>
    );
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          fontSize: 12,
          lineHeight: 1.4,
          cursor: 'pointer',
          border: 'none',
          background: 'none',
          p: 0,
          m: 0,
          fontFamily: 'inherit',
          color: value ? '#2f3a45' : '#8a96a3',
          textDecoration: 'underline dotted',
          textDecorationColor: '#c5ccd6',
          '&:hover': {
            color: 'primary.main',
            textDecorationColor: 'primary.main',
          },
        }}
      >
        {formatDisplayDate(value)}
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mt: 0.5 } } }}
      >
        <DateCalendar
          value={value}
          onChange={(newValue) => {
            onChange(newValue);
            if (newValue) {
              setAnchorEl(null);
            }
          }}
        />
        {value && (
          <Box sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: 'center' }}>
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                onChange(null);
                setAnchorEl(null);
              }}
            >
              Törlés
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
};

const ProjekttervezesContent: React.FC<{
  createPermission?: string;
  editPermission?: string;
  deletePermission?: string;
  focus?: ProjekttervezesFocus | null;
  onFocusHandled?: () => void;
}> = ({ createPermission, editPermission, deletePermission, focus, onFocusHandled }) => {
  const canCreate = createPermission ? permissionService.hasPermission(createPermission) : true;
  const canEdit = editPermission ? permissionService.hasPermission(editPermission) : true;
  const canDelete = deletePermission ? permissionService.hasPermission(deletePermission) : true;
  const [rows, setRows] = useState<ProjekttervezesSor[]>([]);
  const [availableMunkaszamok, setAvailableMunkaszamok] = useState<ProjekttervezesMunkaszamOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [expandedTetelIds, setExpandedTetelIds] = useState<number[]>([]);
  const [expandedCsoportIds, setExpandedCsoportIds] = useState<number[]>([]);
  const [tasksByTetelId, setTasksByTetelId] = useState<Record<number, ProjekttervezesFeladat[]>>({});
  const [groupsByTetelId, setGroupsByTetelId] = useState<Record<number, ProjekttervezesCsoport[]>>({});
  const [feladatDialog, setFeladatDialog] = useState<FeladatDialogState>({
    open: false,
    mode: 'create',
    tetelId: 0,
    csoportId: null,
    feladat: null,
  });
  const [csoportDialog, setCsoportDialog] = useState<CsoportDialogState>({
    open: false,
    mode: 'create',
    tetelId: 0,
    parentCsoportId: null,
    csoport: null,
  });
  const [addItemMenu, setAddItemMenu] = useState<AddItemMenuState | null>(null);
  const [childDragged, setChildDragged] = useState<{
    tetelId: number;
    parentCsoportId: number | null;
    index: number;
  } | null>(null);
  const [childDropTarget, setChildDropTarget] = useState<{
    tetelId: number;
    parentCsoportId: number | null;
    index: number;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false,
    kind: 'project',
    tetelId: 0,
    label: '',
  });
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<ArchiveConfirmState>({
    open: false,
    tetelId: 0,
    label: '',
    nextArchivedState: false,
  });
  const [archiveInProgress, setArchiveInProgress] = useState(false);
  const [filterMode, setFilterMode] = useState<ProjektFilterMode>('none');
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
  const [filterMunkaszamInput, setFilterMunkaszamInput] = useState('');
  const [appliedFilterMode, setAppliedFilterMode] = useState<ProjektFilterMode>('none');
  const [appliedFilterStartDate, setAppliedFilterStartDate] = useState<Date | null>(null);
  const [appliedFilterEndDate, setAppliedFilterEndDate] = useState<Date | null>(null);
  const [appliedFilterMunkaszamQuery, setAppliedFilterMunkaszamQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const leftRowsContainerRef = useRef<HTMLDivElement>(null);
  const dragGhostRef = useRef<HTMLDivElement | null>(null);
  const appliedFocusRef = useRef<string | null>(null);

  const flatRows = useMemo<FlatRow[]>(() => {
    return buildProjekttervezesFlatRows(
      rows,
      expandedTetelIds,
      expandedCsoportIds,
      groupsByTetelId,
      tasksByTetelId,
    );
  }, [rows, expandedTetelIds, expandedCsoportIds, groupsByTetelId, tasksByTetelId]);

  const timeline = useGanttTimeline(flatRows.length);
  const handleViewportWidth = useCallback(
    (width: number) => timeline.setViewportWidth(width),
    [timeline],
  );

  const handleLeftHeaderScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const container = leftRowsContainerRef.current;
    if (container) {
      container.style.setProperty('--left-scroll-offset', `-${event.currentTarget.scrollLeft}px`);
    }
  }, []);

  const munkaszamFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((row) => row.munkaszam)
            .filter((value) => Boolean(value?.trim()))
            .map((value) => value.trim()),
        ),
      ).sort((a, b) => a.localeCompare(b, 'hu-HU')),
    [rows],
  );

  const applyFilters = useCallback((
    nextMode?: ProjektFilterMode,
    nextStartDate?: Date | null,
    nextEndDate?: Date | null,
    nextMunkaszamInput?: string,
  ) => {
    const mode = nextMode ?? filterMode;
    const startDate = nextStartDate !== undefined ? nextStartDate : filterStartDate;
    const endDate = nextEndDate !== undefined ? nextEndDate : filterEndDate;
    const munkaszamInput = nextMunkaszamInput !== undefined ? nextMunkaszamInput : filterMunkaszamInput;

    setAppliedFilterMode(mode);
    setAppliedFilterStartDate(mode === 'dateRange' ? startDate : null);
    setAppliedFilterEndDate(mode === 'dateRange' ? endDate : null);
    setAppliedFilterMunkaszamQuery(mode === 'munkaszam' ? munkaszamInput.trim() : '');
  }, [filterEndDate, filterMode, filterMunkaszamInput, filterStartDate]);

  const loadAvailableMunkaszamok = useCallback(async () => {
    try {
      const options = await projekttervezesService.getAvailableMunkaszamok();
      setAvailableMunkaszamok(options);
    } catch (error) {
      console.error('Elérhető munkaszámok betöltési hiba:', error);
      setAvailableMunkaszamok([]);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const planningRows = await projekttervezesService.getAll({
        includeArchived: showArchived,
        projektKezdeteTol: appliedFilterMode === 'dateRange' ? toApiDate(appliedFilterStartDate) : null,
        projektKezdeteIg: appliedFilterMode === 'dateRange' ? toApiDate(appliedFilterEndDate) : null,
        munkaszamQuery: appliedFilterMode === 'munkaszam' ? appliedFilterMunkaszamQuery : '',
      });
      setRows(planningRows);
      setExpandedTetelIds((current) => (
        current.filter((id) => planningRows.some((row) => row.id === id)).slice(0, 1)
      ));
      setExpandedCsoportIds([]);
      setTasksByTetelId({});
      setGroupsByTetelId({});
      await loadAvailableMunkaszamok();
    } catch (error) {
      console.error('Projekttervezés betöltési hiba:', error);
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Nem sikerült betölteni a projekttervezést.'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [
    appliedFilterEndDate,
    appliedFilterMode,
    appliedFilterMunkaszamQuery,
    appliedFilterStartDate,
    loadAvailableMunkaszamok,
    showArchived,
  ]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!focus) {
      appliedFocusRef.current = null;
    }
  }, [focus]);

  useEffect(() => {
    if (!focus || loading) {
      return;
    }

    const focusKey = `${focus.tetelId}-${focus.feladatId}`;
    if (appliedFocusRef.current === focusKey) {
      return;
    }

    const applyFocus = async () => {
      setExpandedTetelIds([focus.tetelId]);
      setExpandedCsoportIds(Array.from(new Set(focus.csoportPath)));

      try {
        const [feladatok, csoportok] = await Promise.all([
          projekttervezesService.getFeladatok(focus.tetelId),
          projekttervezesService.getCsoportok(focus.tetelId),
        ]);
        setTasksByTetelId((current) => ({ ...current, [focus.tetelId]: feladatok }));
        setGroupsByTetelId((current) => ({ ...current, [focus.tetelId]: csoportok }));

        const task = feladatok.find((item) => item.id === focus.feladatId);
        if (task) {
          setFeladatDialog({
            open: true,
            mode: 'edit',
            tetelId: focus.tetelId,
            csoportId: task.csoportId,
            feladat: task,
          });
        }

        appliedFocusRef.current = focusKey;

        requestAnimationFrame(() => {
          document
            .querySelector(`[data-row-key="task-${focus.feladatId}"]`)
            ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
      } catch (error) {
        console.error('Feladat fókusz navigáció hiba:', error);
        setSnackbar({
          open: true,
          message: getApiErrorMessage(error, 'Nem sikerült megnyitni a kiválasztott feladatot.'),
          severity: 'error',
        });
      } finally {
        onFocusHandled?.();
      }
    };

    applyFocus();
  }, [focus, loading, onFocusHandled]);

  const loadTetelItems = useCallback(async (tetelId: number) => {
    try {
      const [feladatok, csoportok] = await Promise.all([
        projekttervezesService.getFeladatok(tetelId),
        projekttervezesService.getCsoportok(tetelId),
      ]);
      setTasksByTetelId((current) => ({ ...current, [tetelId]: feladatok }));
      setGroupsByTetelId((current) => ({ ...current, [tetelId]: csoportok }));
    } catch (error) {
      console.error('Projekt elemek betöltési hiba:', error);
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Nem sikerült betölteni a projekt elemeit.'), severity: 'error' });
    }
  }, []);

  const toggleExpand = useCallback(async (tetelId: number) => {
    const isExpanded = expandedTetelIds.includes(tetelId);
    if (isExpanded) {
      setExpandedTetelIds((current) => current.filter((id) => id !== tetelId));
      setExpandedCsoportIds([]);
      return;
    }

    setExpandedTetelIds([tetelId]);
    setExpandedCsoportIds([]);
    if (!tasksByTetelId[tetelId] || !groupsByTetelId[tetelId]) {
      await loadTetelItems(tetelId);
    }
  }, [expandedTetelIds, groupsByTetelId, loadTetelItems, tasksByTetelId]);

  const toggleCsoportExpand = useCallback((csoportId: number) => {
    setExpandedCsoportIds((current) => (
      current.includes(csoportId)
        ? current.filter((id) => id !== csoportId)
        : [...current, csoportId]
    ));
  }, []);

  const ensureTetelExpanded = useCallback((tetelId: number) => {
    const shouldResetGroups = expandedTetelIds.length > 0 && !expandedTetelIds.includes(tetelId);
    setExpandedTetelIds([tetelId]);
    if (shouldResetGroups) {
      setExpandedCsoportIds([]);
    }
    if (!tasksByTetelId[tetelId] || !groupsByTetelId[tetelId]) {
      loadTetelItems(tetelId);
    }
  }, [expandedTetelIds, groupsByTetelId, loadTetelItems, tasksByTetelId]);

  const openAddItemMenu = (
    event: React.MouseEvent<HTMLElement>,
    tetelId: number,
    parentCsoportId: number | null,
  ) => {
    event.stopPropagation();
    ensureTetelExpanded(tetelId);
    setAddItemMenu({ anchorEl: event.currentTarget, tetelId, parentCsoportId });
  };

  const openCreateFeladatDialog = (tetelId: number, csoportId: number | null = null) => {
    ensureTetelExpanded(tetelId);
    setFeladatDialog({ open: true, mode: 'create', tetelId, csoportId, feladat: null });
  };

  const openCreateCsoportDialog = (tetelId: number, parentCsoportId: number | null = null) => {
    ensureTetelExpanded(tetelId);
    setCsoportDialog({ open: true, mode: 'create', tetelId, parentCsoportId, csoport: null });
  };

  const openEditFeladatDialog = (tetelId: number, feladat: ProjekttervezesFeladat) => {
    setFeladatDialog({
      open: true,
      mode: 'edit',
      tetelId,
      csoportId: feladat.csoportId,
      feladat,
    });
  };

  const openEditCsoportDialog = (tetelId: number, csoport: ProjekttervezesCsoport) => {
    setCsoportDialog({
      open: true,
      mode: 'edit',
      tetelId,
      parentCsoportId: csoport.parentCsoportId,
      csoport,
    });
  };

  const handleSaveFeladat = async (value: Parameters<typeof feladatDialogValueToPayload>[0]) => {
    const payload = {
      ...feladatDialogValueToPayload(value),
      csoportId: feladatDialog.csoportId,
    };
    const { tetelId, mode, feladat } = feladatDialog;

    if (mode === 'create') {
      const created = await projekttervezesService.addFeladat(tetelId, payload);
      setTasksByTetelId((current) => ({
        ...current,
        [tetelId]: [...(current[tetelId] ?? []), created],
      }));
      setSnackbar({ open: true, message: 'Feladat hozzáadva.', severity: 'success' });
      return;
    }

    if (!feladat) {
      return;
    }

    const updated = await projekttervezesService.updateFeladat(tetelId, feladat.id, payload);
    setTasksByTetelId((current) => ({
      ...current,
      [tetelId]: (current[tetelId] ?? []).map((item) => (item.id === updated.id ? updated : item)),
    }));
    setSnackbar({ open: true, message: 'Feladat mentve.', severity: 'success' });
  };

  const handleSaveCsoport = async (value: Parameters<typeof csoportDialogValueToPayload>[0]) => {
    const basePayload = csoportDialogValueToPayload(value);
    const { tetelId, mode, csoport, parentCsoportId } = csoportDialog;

    if (mode === 'create') {
      const created = await projekttervezesService.addCsoport(tetelId, {
        ...basePayload,
        parentCsoportId,
      });
      setGroupsByTetelId((current) => ({
        ...current,
        [tetelId]: [...(current[tetelId] ?? []), created],
      }));
      setSnackbar({ open: true, message: 'Csoport hozzáadva.', severity: 'success' });
      return;
    }

    if (!csoport) {
      return;
    }

    const updated = await projekttervezesService.updateCsoport(tetelId, csoport.id, {
      ...basePayload,
      parentCsoportId: csoport.parentCsoportId,
    });
    setGroupsByTetelId((current) => ({
      ...current,
      [tetelId]: (current[tetelId] ?? []).map((item) => (item.id === updated.id ? updated : item)),
    }));
    setSnackbar({ open: true, message: 'Csoport mentve.', severity: 'success' });
  };

  const handleRemoveFeladat = async (tetelId: number, feladatId: number) => {
    try {
      await projekttervezesService.removeFeladat(tetelId, feladatId);
      setTasksByTetelId((current) => ({
        ...current,
        [tetelId]: (current[tetelId] ?? []).filter((item) => item.id !== feladatId),
      }));
      setSnackbar({ open: true, message: 'Feladat törölve.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Nem sikerült törölni a feladatot.'), severity: 'error' });
    }
  };

  const openDeleteProjectConfirm = (row: ProjekttervezesSor) => {
    const label = [row.munkaszam, row.munkaTargya].filter(Boolean).join(' – ');
    setDeleteConfirm({
      open: true,
      kind: 'project',
      tetelId: row.id,
      label: label || row.munkaszam,
    });
  };

  const openArchiveConfirm = (row: ProjekttervezesSor) => {
    const label = [row.munkaszam, row.munkaTargya].filter(Boolean).join(' – ');
    setArchiveConfirm({
      open: true,
      tetelId: row.id,
      label: label || row.munkaszam,
      nextArchivedState: !row.isArchived,
    });
  };

  const openDeleteTaskConfirm = (parentRow: ProjekttervezesSor, task: ProjekttervezesFeladat) => {
    setDeleteConfirm({
      open: true,
      kind: 'task',
      tetelId: parentRow.id,
      feladatId: task.id,
      label: task.nev,
    });
  };

  const openDeleteGroupConfirm = (parentRow: ProjekttervezesSor, csoport: ProjekttervezesCsoport) => {
    setDeleteConfirm({
      open: true,
      kind: 'group',
      tetelId: parentRow.id,
      csoportId: csoport.id,
      label: csoport.nev,
    });
  };

  const handleRemoveCsoport = async (tetelId: number, csoportId: number) => {
    try {
      await projekttervezesService.removeCsoport(tetelId, csoportId);
      setExpandedCsoportIds((current) => current.filter((id) => id !== csoportId));
      await loadTetelItems(tetelId);
      setSnackbar({ open: true, message: 'Csoport törölve.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Nem sikerült törölni a csoportot.'), severity: 'error' });
    }
  };

  const closeDeleteConfirm = () => {
    if (deleteInProgress) {
      return;
    }
    setDeleteConfirm((prev) => ({ ...prev, open: false }));
  };

  const closeArchiveConfirm = () => {
    if (archiveInProgress) {
      return;
    }
    setArchiveConfirm((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteConfirm = async () => {
    setDeleteInProgress(true);
    try {
      if (deleteConfirm.kind === 'project') {
        await handleRemove(deleteConfirm.tetelId);
      } else if (deleteConfirm.kind === 'group' && deleteConfirm.csoportId != null) {
        await handleRemoveCsoport(deleteConfirm.tetelId, deleteConfirm.csoportId);
      } else if (deleteConfirm.feladatId != null) {
        await handleRemoveFeladat(deleteConfirm.tetelId, deleteConfirm.feladatId);
      }
      setDeleteConfirm((prev) => ({ ...prev, open: false }));
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleArchiveConfirm = async () => {
    setArchiveInProgress(true);
    try {
      const updated = await projekttervezesService.setArchived(
        archiveConfirm.tetelId,
        archiveConfirm.nextArchivedState,
      );

      setRows((current) => {
        if (!showArchived && updated.isArchived) {
          return current.filter((row) => row.id !== updated.id);
        }

        return current.map((row) => (row.id === updated.id ? updated : row));
      });

      setArchiveConfirm((prev) => ({ ...prev, open: false }));
      setSnackbar({
        open: true,
        message: archiveConfirm.nextArchivedState
          ? 'Projekt archiválva.'
          : 'Projekt visszaállítva az archívból.',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(
          error,
          archiveConfirm.nextArchivedState
            ? 'Nem sikerült archiválni a projektet.'
            : 'Nem sikerült visszaállítani a projektet.'
        ),
        severity: 'error',
      });
    } finally {
      setArchiveInProgress(false);
    }
  };

  const handleFeladatDatesChange = async (
    tetelId: number,
    feladatId: number,
    kezdetiDatum: Date | null,
    befejezesDatum: Date | null,
  ) => {
    try {
      const updated = await projekttervezesService.updateFeladatDates(tetelId, feladatId, {
        kezdetiDatum: toApiDate(kezdetiDatum),
        befejezesDatum: toApiDate(befejezesDatum),
      });
      setTasksByTetelId((current) => ({
        ...current,
        [tetelId]: (current[tetelId] ?? []).map((item) => (item.id === updated.id ? updated : item)),
      }));
    } catch (error) {
      await loadTetelItems(tetelId);
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Nem sikerült menteni a dátumokat.'), severity: 'error' });
    }
  };

  const reorderItems = (tetelId: number, parentCsoportId: number | null, fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }

    const groups = groupsByTetelId[tetelId] ?? [];
    const tasks = tasksByTetelId[tetelId] ?? [];
    const merged = getMergedSiblingItems(parentCsoportId, groups, tasks);
    const reordered = [...merged];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    const nextGroups = [...groups];
    const nextTasks = [...tasks];
    reordered.forEach((item, index) => {
      if (item.kind === 'group') {
        const groupIndex = nextGroups.findIndex((group) => group.id === item.group.id);
        if (groupIndex >= 0) {
          nextGroups[groupIndex] = { ...nextGroups[groupIndex], sortOrder: index };
        }
        return;
      }

      const taskIndex = nextTasks.findIndex((task) => task.id === item.task.id);
      if (taskIndex >= 0) {
        nextTasks[taskIndex] = { ...nextTasks[taskIndex], sortOrder: index };
      }
    });

    setGroupsByTetelId((current) => ({ ...current, [tetelId]: nextGroups }));
    setTasksByTetelId((current) => ({ ...current, [tetelId]: nextTasks }));

    projekttervezesService.reorderItems(tetelId, {
      parentCsoportId,
      items: reordered.map((item) => ({
        kind: item.kind,
        id: item.id,
      })),
    }).catch(async (error) => {
      await loadTetelItems(tetelId);
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Nem sikerült menteni a sorrendet.'), severity: 'error' });
    });
  };

  const handleAdd = async (munkaszamId: number) => {
    try {
      await projekttervezesService.add(munkaszamId);
      setDialogOpen(false);
      await loadRows();
      setSnackbar({ open: true, message: 'Munkaszám hozzáadva a projekttervezéshez.', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Nem sikerült hozzáadni a munkaszámot.'), severity: 'error' });
    }
  };

  const handleRemove = async (tetelId: number) => {
    try {
      await projekttervezesService.remove(tetelId);
      setExpandedTetelIds((current) => current.filter((id) => id !== tetelId));
      setExpandedCsoportIds([]);
      setTasksByTetelId((current) => {
        const next = { ...current };
        delete next[tetelId];
        return next;
      });
      setGroupsByTetelId((current) => {
        const next = { ...current };
        delete next[tetelId];
        return next;
      });
      await loadRows();
      setSnackbar({ open: true, message: 'Tétel eltávolítva a projekttervezésből.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Nem sikerült eltávolítani a tételt.'), severity: 'error' });
    }
  };

  const persistReorder = async (reordered: ProjekttervezesSor[]) => {
    try {
      await projekttervezesService.reorder(reordered.map((row) => row.id));
    } catch (error) {
      await loadRows();
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Nem sikerült menteni a sorrendet.'), severity: 'error' });
    }
  };

  const reorderRows = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }

    const reordered = [...rows];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setRows(reordered);
    persistReorder(reordered);
  };

  const updateRow = async (
    row: ProjekttervezesSor,
    changes: {
      haladas?: number | null;
      tenylegesProjektKezdete?: Date | null;
      tenylegesBefejezes?: Date | null;
    },
  ) => {
    try {
      const updated = await projekttervezesService.update(row.id, {
        haladas: changes.haladas !== undefined ? changes.haladas : row.haladas,
        tenylegesProjektKezdete: changes.tenylegesProjektKezdete !== undefined
          ? toApiDate(changes.tenylegesProjektKezdete)
          : toApiDate(parseApiDate(row.tenylegesProjektKezdete)),
        tenylegesBefejezes: changes.tenylegesBefejezes !== undefined
          ? toApiDate(changes.tenylegesBefejezes)
          : toApiDate(parseApiDate(row.tenylegesBefejezes)),
      });

      setRows((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (error) {
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Nem sikerült menteni a módosítást.'), severity: 'error' });
    }
  };

  const legendItems = useMemo(
    () => [
      { color: '#d9dde3', label: 'Terv időtartama' },
      { color: '#9aa3ad', label: 'Tényleges eltelt idő' },
      { color: '#f2d58c', label: 'Terven túli folyamat' },
      { color: '#e53935', label: 'Tervezett befejezés / mai nap' },
    ],
    [],
  );
  const isProjectReorderEnabled = appliedFilterMode === 'none';

  const leftPanelBaseSx = {
    width: LEFT_PANEL_MAX_WIDTH,
    minWidth: 240,
    maxWidth: LEFT_PANEL_MAX_WIDTH,
    flexShrink: 0,
    borderRight: `1px solid ${ROW_DIVIDER_COLOR}`,
  };

  const leftHeaderScrollSx = {
    ...leftPanelBaseSx,
    overflowX: 'auto' as const,
    overflowY: 'hidden' as const,
    '&::-webkit-scrollbar': { height: 6 },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#c5ccd6', borderRadius: 3 },
  };

  const getChildDragProps = (tetelId: number, parentCsoportId: number | null, index: number) => ({
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (
        childDragged
        && childDragged.tetelId === tetelId
        && childDragged.parentCsoportId === parentCsoportId
        && childDragged.index !== index
      ) {
        setChildDropTarget({ tetelId, parentCsoportId, index });
      }
    },
    onDragLeave: () => {
      if (
        childDropTarget?.tetelId === tetelId
        && childDropTarget.parentCsoportId === parentCsoportId
        && childDropTarget.index === index
      ) {
        setChildDropTarget(null);
      }
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (
        childDragged
        && childDragged.tetelId === tetelId
        && childDragged.parentCsoportId === parentCsoportId
      ) {
        reorderItems(tetelId, parentCsoportId, childDragged.index, index);
      }
      setChildDragged(null);
      setChildDropTarget(null);
    },
  });

  const handleChildDragStart = (
    event: React.DragEvent,
    tetelId: number,
    parentCsoportId: number | null,
    itemId: number,
    index: number,
  ) => {
    event.stopPropagation();
    setChildDragged({ tetelId, parentCsoportId, index });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(itemId));
  };

  const handleChildDragEnd = () => {
    setChildDragged(null);
    setChildDropTarget(null);
  };

  const getRowDragProps = (index: number) => ({
    onDragOver: !isProjectReorderEnabled ? undefined : (event: React.DragEvent) => {
      event.preventDefault();
      if (draggedIndex !== null && draggedIndex !== index) {
        setDropTargetIndex(index);
      }
    },
    onDragLeave: !isProjectReorderEnabled ? undefined : () => {
      if (dropTargetIndex === index) {
        setDropTargetIndex(null);
      }
    },
    onDrop: !isProjectReorderEnabled ? undefined : (event: React.DragEvent) => {
      event.preventDefault();
      if (draggedIndex !== null) {
        reorderRows(draggedIndex, index);
      }
      setDraggedIndex(null);
      setDropTargetIndex(null);
    },
  });

  const cleanupDragGhost = useCallback(() => {
    if (dragGhostRef.current) {
      dragGhostRef.current.remove();
      dragGhostRef.current = null;
    }
  }, []);

  const handleRowDragStart = useCallback((
    event: React.DragEvent,
    row: ProjekttervezesSor,
    index: number,
  ) => {
    event.stopPropagation();
    setDraggedIndex(index);
    event.dataTransfer.effectAllowed = 'move';

    cleanupDragGhost();

    const isCompleted = !!row.tenylegesBefejezes;
    const label = [row.munkaszam, row.munkaTargya].filter(Boolean).join(' – ');
    const ghost = document.createElement('div');
    ghost.textContent = label.length > 72 ? `${label.slice(0, 69)}…` : label;
    Object.assign(ghost.style, {
      position: 'fixed',
      top: '-1000px',
      left: '-1000px',
      width: '560px',
      height: `${GANTT_ROW_HEIGHT}px`,
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      boxSizing: 'border-box',
      backgroundColor: isCompleted ? COMPLETED_ROW_BG : '#fff',
      border: '1px solid #90caf9',
      borderRadius: '4px',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.14)',
      fontSize: '12px',
      fontWeight: '600',
      color: '#2f3a45',
      pointerEvents: 'none',
      zIndex: '10000',
      opacity: '0.96',
    });

    document.body.appendChild(ghost);
    dragGhostRef.current = ghost;
    event.dataTransfer.setDragImage(ghost, 28, GANTT_ROW_HEIGHT / 2);
  }, [cleanupDragGhost]);

  const handleRowDragEnd = useCallback(() => {
    cleanupDragGhost();
    setDraggedIndex(null);
    setDropTargetIndex(null);
  }, [cleanupDragGhost]);

  const renderItemAddButton = (tetelId: number, parentCsoportId: number | null) => {
    if (!canCreate) {
      return null;
    }

    return (
      <Tooltip title="Item hozzáadása">
        <IconButton
          size="small"
          color="primary"
          onClick={(event) => openAddItemMenu(event, tetelId, parentCsoportId)}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  const renderLeftCells = (row: ProjekttervezesSor, index: number) => {
    const isExpanded = expandedTetelIds.includes(row.id);

    return (
      <>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[0].width }}>
          {canDelete && (
            <Tooltip title="Eltávolítás">
              <IconButton size="small" color="error" onClick={() => openDeleteProjectConfirm(row)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title={row.isArchived ? 'Visszaállítás archívból' : 'Archiválás'}>
              <IconButton
                size="small"
                color={row.isArchived ? 'success' : 'default'}
                onClick={() => openArchiveConfirm(row)}
              >
                {row.isArchived ? <UnarchiveOutlinedIcon fontSize="small" /> : <ArchiveOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {renderItemAddButton(row.id, null)}
          {canEdit && isProjectReorderEnabled && (
            <Tooltip title="Húzd a sorrendezéshez">
              <IconButton
                size="small"
                draggable
                onDragStart={(event) => handleRowDragStart(event, row, index)}
                onDragEnd={handleRowDragEnd}
                sx={{ cursor: 'grab', color: '#8a96a3', '&:active': { cursor: 'grabbing' } }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            ...bodyCellSx,
            width: LEFT_COLUMNS[1].width,
            cursor: 'pointer',
            justifyContent: 'flex-start',
            gap: 0.25,
            px: 0.75,
          }}
          onClick={() => toggleExpand(row.id)}
        >
          {isExpanded ? (
            <ExpandMoreIcon sx={{ fontSize: 16, color: '#8a96a3', flexShrink: 0 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16, color: '#8a96a3', flexShrink: 0 }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }} noWrap>
            {row.munkaszam}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[2].width }}>
          <Typography variant="body2" noWrap title={row.munkaTargya} sx={{ fontSize: 12, width: '100%' }}>
            {row.munkaTargya || '–'}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[3].width }}>
          <Typography variant="body2" noWrap title={row.ugyintezoNev} sx={{ fontSize: 12, width: '100%' }}>
            {row.ugyintezoNev || '–'}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[4].width }}>
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            {formatDisplayDate(parseApiDate(row.projektKezdete))}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[5].width }}>
          <CompactTableDatePicker
            value={parseApiDate(row.tenylegesProjektKezdete)}
            onChange={(value) => updateRow(row, { tenylegesProjektKezdete: value })}
            disabled={!canEdit}
          />
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[6].width }}>
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            {formatDisplayDate(parseApiDate(row.tervezettBefejezes))}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[7].width }}>
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            {row.hatralevoMunkanapok ?? '–'}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[8].width }}>
          <CompactTableDatePicker
            value={parseApiDate(row.tenylegesBefejezes)}
            onChange={(value) => updateRow(row, { tenylegesBefejezes: value })}
            disabled={!canEdit}
          />
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[9].width, borderRight: 'none' }}>
          <TextField
            size="small"
            type="number"
            value={row.haladas ?? ''}
            disabled={!canEdit}
            inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
            onChange={(event) => {
              const raw = event.target.value;
              const parsed = raw === '' ? null : Number(raw);
              if (parsed != null && (parsed < 0 || parsed > 100)) {
                return;
              }
              setRows((current) =>
                current.map((item) =>
                  item.id === row.id ? { ...item, haladas: parsed } : item,
                ),
              );
            }}
            onBlur={() => updateRow(row, { haladas: row.haladas })}
            sx={{ width: 52, '& .MuiInputBase-input': { py: 0.75, fontSize: 12 } }}
          />
        </Box>
      </>
    );
  };

  const renderEmptyDataCells = () => (
    <>
      <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[4].width }}>
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>–</Typography>
      </Box>
      <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[5].width }}>
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>–</Typography>
      </Box>
      <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[6].width }}>
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>–</Typography>
      </Box>
      <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[7].width }}>
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>–</Typography>
      </Box>
      <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[8].width }}>
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>–</Typography>
      </Box>
      <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[9].width, borderRight: 'none' }}>
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>–</Typography>
      </Box>
    </>
  );

  const renderGroupLeftCells = (
    csoport: ProjekttervezesCsoport,
    parentRow: ProjekttervezesSor,
    _depth: number,
    siblingIndex: number,
  ) => {
    const isExpanded = expandedCsoportIds.includes(csoport.id);

    return (
      <>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[0].width, pl: 1 }}>
          {canDelete && (
            <Tooltip title="Törlés">
              <IconButton
                size="small"
                color="error"
                onClick={(event) => {
                  event.stopPropagation();
                  openDeleteGroupConfirm(parentRow, csoport);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {renderItemAddButton(parentRow.id, csoport.id)}
          {canEdit && (
            <Tooltip title="Húzd a sorrendezéshez">
              <IconButton
                size="small"
                draggable
                onDragStart={(event) => handleChildDragStart(
                  event,
                  parentRow.id,
                  csoport.parentCsoportId,
                  csoport.id,
                  siblingIndex,
                )}
                onDragEnd={handleChildDragEnd}
                sx={{ cursor: 'grab', color: '#8a96a3', '&:active': { cursor: 'grabbing' } }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            ...bodyCellSx,
            width: LEFT_COLUMNS[1].width,
            px: 0.75,
            justifyContent: 'flex-start',
            cursor: 'pointer',
            gap: 0.25,
          }}
          onClick={() => toggleCsoportExpand(csoport.id)}
        >
          {isExpanded ? (
            <ExpandMoreIcon sx={{ fontSize: 16, color: '#8a96a3', flexShrink: 0 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16, color: '#8a96a3', flexShrink: 0 }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }} noWrap title={csoport.nev}>
            {csoport.nev}
          </Typography>
        </Box>
        <Box
          sx={{ ...bodyCellSx, width: LEFT_COLUMNS[2].width, cursor: canEdit ? 'pointer' : 'default' }}
          onClick={() => {
            if (canEdit) {
              openEditCsoportDialog(parentRow.id, csoport);
            }
          }}
        >
          <Typography variant="body2" noWrap title={csoport.leiras} sx={{ fontSize: 12, width: '100%' }}>
            {csoport.leiras || '–'}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[3].width }}>
          <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>–</Typography>
        </Box>
        {renderEmptyDataCells()}
      </>
    );
  };

  const renderTaskLeftCells = (
    task: ProjekttervezesFeladat,
    parentRow: ProjekttervezesSor,
    _depth: number,
    siblingIndex: number,
    parentCsoportId: number | null,
  ) => {
    const felelosLabel = task.felelosok.length
      ? task.felelosok.map((felelos) => felelos.userName).join(', ')
      : '–';

    return (
      <>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[0].width, pl: 1 }}>
          {canDelete && (
            <Tooltip title="Törlés">
              <IconButton
                size="small"
                color="error"
                onClick={(event) => {
                  event.stopPropagation();
                  openDeleteTaskConfirm(parentRow, task);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title="Húzd a sorrendezéshez">
              <IconButton
                size="small"
                draggable
                onDragStart={(event) => handleChildDragStart(
                  event,
                  parentRow.id,
                  parentCsoportId,
                  task.id,
                  siblingIndex,
                )}
                onDragEnd={handleChildDragEnd}
                sx={{ cursor: 'grab', color: '#8a96a3', '&:active': { cursor: 'grabbing' } }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            ...bodyCellSx,
            width: LEFT_COLUMNS[1].width,
            px: 0.75,
            justifyContent: 'flex-start',
            cursor: canEdit ? 'pointer' : 'default',
            gap: 0.25,
          }}
          onClick={() => {
            if (canEdit) {
              openEditFeladatDialog(parentRow.id, task);
            }
          }}
        >
          <Box sx={{ width: 16, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12, cursor: 'pointer' }} noWrap title={task.nev}>
            {task.nev}
          </Typography>
        </Box>
        <Box
          sx={{ ...bodyCellSx, width: LEFT_COLUMNS[2].width, cursor: canEdit ? 'pointer' : 'default' }}
          onClick={() => {
            if (canEdit) {
              openEditFeladatDialog(parentRow.id, task);
            }
          }}
        >
          <Typography variant="body2" noWrap title={task.leiras} sx={{ fontSize: 12, width: '100%' }}>
            {task.leiras || '–'}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[3].width }}>
          <Typography variant="body2" sx={{ fontSize: 11 }} noWrap title={felelosLabel}>
            {felelosLabel}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[4].width }}>
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            {formatDisplayDate(parseApiDate(task.kezdetiDatum))}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[5].width }}>
          <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
            –
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[6].width }}>
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            {formatDisplayDate(parseApiDate(task.befejezesDatum))}
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[7].width }}>
          <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
            –
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[8].width }}>
          <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
            –
          </Typography>
        </Box>
        <Box sx={{ ...bodyCellSx, width: LEFT_COLUMNS[9].width, borderRight: 'none' }}>
          <Typography variant="body2" sx={{ fontSize: 11 }} noWrap title={PROJEKTTERVEZES_FELADAT_STATUS_LABELS[task.status]}>
            {PROJEKTTERVEZES_FELADAT_STATUS_LABELS[task.status]}
          </Typography>
        </Box>
      </>
    );
  };

  const getFlatRowBackground = (flatRow: FlatRow, isDragging: boolean, isDropTarget: boolean) => {
    if (flatRow.kind === 'task' || flatRow.kind === 'group') {
      return {
        backgroundColor: CHILD_ROW_BG,
        opacity: isDragging ? 0.35 : 1,
        boxShadow: isDropTarget ? 'inset 0 2px 0 #1976d2' : 'none',
      };
    }

    const isArchived = flatRow.row.isArchived;
    const isCompleted = !!flatRow.row.tenylegesBefejezes;
    return {
      backgroundColor: isArchived ? ARCHIVED_ROW_BG : (isCompleted ? COMPLETED_ROW_BG : '#fff'),
      opacity: isDragging ? 0.35 : 1,
      boxShadow: isDropTarget ? 'inset 0 2px 0 #1976d2' : 'none',
    };
  };

  const flatRowSx = {
    display: 'flex',
    height: GANTT_ROW_HEIGHT,
    minHeight: GANTT_ROW_HEIGHT,
    borderBottom: `1px solid ${ROW_DIVIDER_COLOR}`,
    transition: 'opacity 0.12s ease, box-shadow 0.12s ease',
    boxSizing: 'border-box' as const,
    overflow: 'hidden',
  };

  const flatRowLeftSx = {
    ...leftPanelBaseSx,
    overflow: 'hidden' as const,
    height: GANTT_ROW_HEIGHT,
    flexShrink: 0,
  };

  const flatRowLeftSlideSx = {
    minWidth: LEFT_TABLE_MIN_WIDTH,
    height: GANTT_ROW_HEIGHT,
    display: 'flex',
    transform: 'translate3d(var(--left-scroll-offset, 0px), 0, 0)',
    willChange: 'transform' as const,
  };

  const renderFlatRow = (flatRow: FlatRow) => {
    if (flatRow.kind === 'project') {
      const { row, projectIndex } = flatRow;
      const isDragging = draggedIndex === projectIndex;
      const isDropTarget = dropTargetIndex === projectIndex && draggedIndex !== projectIndex;
      const isExpanded = expandedTetelIds.includes(row.id);
      const rowStyles = getFlatRowBackground(flatRow, isDragging, isDropTarget);

      return (
        <Box
          key={`row-project-${row.id}`}
          {...getRowDragProps(projectIndex)}
          sx={{
            ...flatRowSx,
            ...rowStyles,
            ...(isExpanded ? { position: 'relative', zIndex: 1 } : {}),
          }}
        >
          <Box sx={flatRowLeftSx}>
            <Box sx={flatRowLeftSlideSx}>{renderLeftCells(row, projectIndex)}</Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, height: GANTT_ROW_HEIGHT, position: 'relative' }}>
            <GanttRowBars
              row={row}
              visibleRange={timeline.visibleRange}
              zoomLevel={timeline.zoomLevel}
              viewportWidth={timeline.viewportWidth}
            />
          </Box>
          {isExpanded && <ExpandedRowOutline />}
        </Box>
      );
    }

    if (flatRow.kind === 'group') {
      const { csoport, parentRow, depth, siblingIndex, parentCsoportId } = flatRow;
      const isDragging = childDragged?.tetelId === parentRow.id
        && childDragged.parentCsoportId === parentCsoportId
        && childDragged.index === siblingIndex;
      const isDropTarget = childDropTarget?.tetelId === parentRow.id
        && childDropTarget.parentCsoportId === parentCsoportId
        && childDropTarget.index === siblingIndex
        && childDragged?.index !== siblingIndex;
      const isExpanded = expandedCsoportIds.includes(csoport.id);
      const rowStyles = getFlatRowBackground(flatRow, isDragging, isDropTarget);
      const groups = groupsByTetelId[parentRow.id] ?? [];
      const tasks = tasksByTetelId[parentRow.id] ?? [];
      const dateRange = computeCsoportDateRange(csoport.id, groups, tasks);

      return (
        <Box
          key={`row-group-${csoport.id}`}
          {...getChildDragProps(parentRow.id, parentCsoportId, siblingIndex)}
          sx={{
            ...flatRowSx,
            ...rowStyles,
            ...(isExpanded ? { position: 'relative', zIndex: 1 } : {}),
          }}
        >
          <Box sx={flatRowLeftSx}>
            <Box sx={flatRowLeftSlideSx}>
              {renderGroupLeftCells(csoport, parentRow, depth, siblingIndex)}
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, height: GANTT_ROW_HEIGHT, position: 'relative' }}>
            <GanttCsoportBar
              szin={csoport.szin}
              startDate={dateRange.start}
              endDate={dateRange.end}
              visibleRange={timeline.visibleRange}
              zoomLevel={timeline.zoomLevel}
              viewportWidth={timeline.viewportWidth}
            />
          </Box>
          {isExpanded && <ExpandedRowOutline />}
        </Box>
      );
    }

    const { task, parentRow, depth, siblingIndex, parentCsoportId } = flatRow;
    const isDragging = childDragged?.tetelId === parentRow.id
      && childDragged.parentCsoportId === parentCsoportId
      && childDragged.index === siblingIndex;
    const isDropTarget = childDropTarget?.tetelId === parentRow.id
      && childDropTarget.parentCsoportId === parentCsoportId
      && childDropTarget.index === siblingIndex
      && childDragged?.index !== siblingIndex;
    const rowStyles = getFlatRowBackground(flatRow, isDragging, isDropTarget);

    return (
      <Box
        key={`row-task-${task.id}`}
        data-row-key={`task-${task.id}`}
        {...getChildDragProps(parentRow.id, parentCsoportId, siblingIndex)}
        sx={{ ...flatRowSx, ...rowStyles }}
      >
        <Box sx={flatRowLeftSx}>
          <Box sx={flatRowLeftSlideSx}>
            {renderTaskLeftCells(task, parentRow, depth, siblingIndex, parentCsoportId)}
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, height: GANTT_ROW_HEIGHT }}>
          <GanttFeladatBar
            feladat={task}
            visibleRange={timeline.visibleRange}
            zoomLevel={timeline.zoomLevel}
            viewportWidth={timeline.viewportWidth}
            readOnly={!canEdit}
            onDatesChange={(feladatId, kezdetiDatum, befejezesDatum) =>
              handleFeladatDatesChange(parentRow.id, feladatId, kezdetiDatum, befejezesDatum)
            }
          />
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2f3a45' }}>
              Projekttervezés
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!canCreate}
            onClick={async () => {
              await loadAvailableMunkaszamok();
              setDialogOpen(true);
            }}
          >
            Munkaszám hozzáadása
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {legendItems.map((item) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 10, borderRadius: 0.5, backgroundColor: item.color }} />
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Szűrő"
            value={filterMode}
            onChange={(event) => {
              const nextMode = event.target.value as ProjektFilterMode;
              setFilterMode(nextMode);
              if (nextMode === 'none') {
                applyFilters('none', null, null, '');
              }
            }}
            sx={{ width: 220 }}
          >
            <MenuItem value="none">Nincs szűrés</MenuItem>
            <MenuItem value="dateRange">Kezdeti dátum intervallum</MenuItem>
            <MenuItem value="munkaszam">Munkaszám</MenuItem>
          </TextField>

          {filterMode === 'dateRange' && (
            <>
              <DatePicker
                label="Kezdet (tól)"
                value={filterStartDate}
                onChange={(value) => setFilterStartDate(value)}
                onAccept={(value) => {
                  setFilterStartDate(value);
                  applyFilters('dateRange', value, filterEndDate);
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="Kezdet (ig)"
                value={filterEndDate}
                onChange={(value) => setFilterEndDate(value)}
                onAccept={(value) => {
                  setFilterEndDate(value);
                  applyFilters('dateRange', filterStartDate, value);
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
            </>
          )}

          {filterMode === 'munkaszam' && (
            <Autocomplete
              freeSolo
              options={munkaszamFilterOptions}
              value={filterMunkaszamInput}
              onChange={(_, value) => {
                const selected = String(value || '');
                setFilterMunkaszamInput(selected);
                if (selected.trim()) {
                  applyFilters('munkaszam', undefined, undefined, selected);
                }
              }}
              onInputChange={(_, value, reason) => {
                setFilterMunkaszamInput(value);
                if (reason === 'clear') {
                  applyFilters('munkaszam', undefined, undefined, '');
                }
              }}
              sx={{ width: 240 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Munkaszám"
                  placeholder="pl. 24-001/0123"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      applyFilters('munkaszam');
                    }
                  }}
                />
              )}
            />
          )}

          <Button
            size="small"
            onClick={() => {
              setFilterMode('none');
              setFilterStartDate(null);
              setFilterEndDate(null);
              setFilterMunkaszamInput('');
              applyFilters('none', null, null, '');
            }}
          >
            Szűrők törlése
          </Button>

          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={showArchived}
                onChange={(_, checked) => setShowArchived(checked)}
              />
            )}
            label="Archivált projektek megjelenítése"
            sx={{ ml: 0.5 }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <GanttTimelineControls timeline={timeline} />
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              border: '1px solid #d0d7de',
              borderRadius: 1,
              overflow: 'auto',
              backgroundColor: '#fff',
            }}
          >
            <Box sx={{ minWidth: 320, display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 5,
                  display: 'flex',
                  backgroundColor: '#f8f9fb',
                  borderBottom: `1px solid ${ROW_DIVIDER_COLOR}`,
                }}
              >
                <Box
                  onScroll={handleLeftHeaderScroll}
                  sx={{ ...leftHeaderScrollSx, height: timeline.headerHeight }}
                >
                  <Box sx={{ minWidth: LEFT_TABLE_MIN_WIDTH, display: 'flex', height: timeline.headerHeight }}>
                    {LEFT_COLUMNS.map((column, index) => (
                      <Box
                        key={column.key}
                        sx={{
                          ...headerCellSx,
                          width: column.width,
                          borderRight: index === LEFT_COLUMNS.length - 1 ? 'none' : headerCellSx.borderRight,
                        }}
                      >
                        {column.label}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <GanttViewportMeasure onWidthChange={handleViewportWidth}>
                  <GanttTimelineHeader timeline={timeline} viewportWidth={timeline.viewportWidth} />
                </GanttViewportMeasure>
              </Box>

              <Box ref={leftRowsContainerRef} sx={{ display: 'flex', flexDirection: 'column' }}>
                {flatRows.map(renderFlatRow)}
              </Box>

              {rows.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  Még nincs munkaszám a projekttervezésben. Adj hozzá egyet a fenti gombbal.
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <AddProjekttervezesDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          options={availableMunkaszamok}
          onAdd={handleAdd}
        />

        <AddProjekttervezesFeladatDialog
          open={feladatDialog.open}
          mode={feladatDialog.mode}
          initialFeladat={feladatDialog.feladat}
          onClose={() => setFeladatDialog((prev) => ({ ...prev, open: false }))}
          onSave={handleSaveFeladat}
        />

        <AddProjekttervezesCsoportDialog
          open={csoportDialog.open}
          mode={csoportDialog.mode}
          initialCsoport={csoportDialog.csoport}
          onClose={() => setCsoportDialog((prev) => ({ ...prev, open: false }))}
          onSave={handleSaveCsoport}
        />

        <Menu
          anchorEl={addItemMenu?.anchorEl ?? null}
          open={Boolean(addItemMenu)}
          onClose={() => setAddItemMenu(null)}
        >
          <MenuItem
            onClick={() => {
              if (!addItemMenu) {
                return;
              }
              openCreateFeladatDialog(addItemMenu.tetelId, addItemMenu.parentCsoportId);
              setAddItemMenu(null);
            }}
          >
            Feladat
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!addItemMenu) {
                return;
              }
              openCreateCsoportDialog(addItemMenu.tetelId, addItemMenu.parentCsoportId);
              setAddItemMenu(null);
            }}
          >
            Csoport
          </MenuItem>
        </Menu>

        <Dialog open={deleteConfirm.open} onClose={closeDeleteConfirm} maxWidth="xs" fullWidth>
          <DialogTitle>
            {deleteConfirm.kind === 'project' && 'Munkaszám eltávolítása'}
            {deleteConfirm.kind === 'group' && 'Csoport törlése'}
            {deleteConfirm.kind === 'task' && 'Feladat törlése'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Biztosan törölni szeretnéd
              {' '}
              <strong>{deleteConfirm.label}</strong>
              {' '}
              {deleteConfirm.kind === 'project'
                ? 'elemet a projekttervezésből? A hozzá tartozó csoportok és feladatok is törlődnek.'
                : deleteConfirm.kind === 'group'
                  ? 'csoportot? Az alcsoportok és feladatok is törlődnek.'
                  : 'feladatot?'}
              {' '}
              Ez a művelet nem vonható vissza.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteConfirm} disabled={deleteInProgress}>
              Mégse
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleteInProgress}
            >
              Törlés
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={archiveConfirm.open} onClose={closeArchiveConfirm} maxWidth="xs" fullWidth>
          <DialogTitle>
            {archiveConfirm.nextArchivedState ? 'Projekt archiválása' : 'Projekt visszaállítása'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Biztosan
              {' '}
              {archiveConfirm.nextArchivedState ? 'archiválni szeretnéd' : 'vissza szeretnéd hozni archívból'}
              {' '}
              <strong>{archiveConfirm.label}</strong>
              {' '}
              projektet?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeArchiveConfirm} disabled={archiveInProgress}>
              Mégse
            </Button>
            <Button
              onClick={handleArchiveConfirm}
              color={archiveConfirm.nextArchivedState ? 'warning' : 'success'}
              variant="contained"
              disabled={archiveInProgress}
            >
              {archiveConfirm.nextArchivedState ? 'Archiválás' : 'Visszaállítás'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default ProjekttervezesContent;
